/**
 * A position in the mushaf: surah, ayah, and a word inside that ayah.
 *
 * Source-independent ON PURPOSE. The design keeps QuranTextSource as the only
 * module that knows where the text came from, so swapping Tanzil for KFGQPC
 * once it is licensed changes rendering and pagination and not a single
 * anchor. Page numbers are deliberately NOT part of this: they are a property
 * of one particular printed mushaf, and encoding them here would make the
 * swap a data migration.
 */
export type Anchor = {
  surah: number;
  ayah: number;
  /** 0-based word within the ayah. */
  word: number;
};

/** The wire form: "surah:ayah:word". Compact, because it is sent per word. */
export function formatAnchor(a: Anchor): string {
  return `${a.surah}:${a.ayah}:${a.word}`;
}

/**
 * Parses the wire form, returning null for anything it does not fully trust.
 *
 * Null rather than a throw or a default: this parses data arriving off the
 * network from another device, and a malformed payload must move nobody's
 * position rather than jumping every student to 1:1:0.
 */
export function parseAnchor(raw: unknown): Anchor | null {
  if (typeof raw !== 'string') return null;
  const parts = raw.split(':');
  if (parts.length !== 3) return null;
  // Number() accepts '', ' 2', '2e1' and '0x2'; a strict digit test does not.
  if (!parts.every((p) => /^\d+$/.test(p))) return null;
  const [surah, ayah, word] = parts.map((p) => Number(p));
  if (surah < 1 || surah > 114) return null;
  if (ayah < 1) return null;
  return { surah, ayah, word };
}

/**
 * Whether two anchors are in the same AYAH, whatever word they point at.
 *
 * This is what the highlight uses. A معلم says "we are at 2:15" and follows a
 * whole verse with their eye, not one word of it — marking a single word made
 * the student hunt for a highlight the size of a thumbnail, and made every
 * word the معلم passed a separate visible jump. The word survives in the
 * anchor because the wire form and the progress record both carry it.
 */
export function sameAyah(a: Anchor | null, b: Anchor | null): boolean {
  if (!a || !b) return a === b;
  return a.surah === b.surah && a.ayah === b.ayah;
}

export function sameAnchor(a: Anchor | null, b: Anchor | null): boolean {
  if (!a || !b) return a === b;
  return a.surah === b.surah && a.ayah === b.ayah && a.word === b.word;
}

/**
 * Splits an ayah into the words a wordIndex counts.
 *
 * One place, because the marker and the sender must agree exactly: if the
 * renderer split on a different rule than the index was produced with, the
 * highlight would drift a word further along with every line.
 */
export function wordsOf(ayahText: string): string[] {
  return ayahText.trim().split(/\s+/).filter((w) => w.length > 0);
}
